import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/shared/api/client';
import { BRAND, SLOGAN } from '@/shared/branding';
import { LockIcon, EyeIcon, ArrowRight } from './authIcons';
import { forgetBootUrl } from '@/bootEntry';

// Landing page for the emailed reset link (/reset-password?token=...).
// Mirrors the LoginPage card so the two feel like one flow.
export function ResetPasswordPage() {
  // From the in-memory route: the app puts the address bar back to "/" once it
  // has started, so the token is no longer there.
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') || '';
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  // No token in the URL = a mangled link; same dead-end as an expired one.
  const [invalidLink, setInvalidLink] = useState(!token);
  const nav = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (pw1.length < 12) { setErr('Password must be at least 12 characters and not a common word or your email'); return; }
    if (pw1 !== pw2) { setErr('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw1 });
      // The token is spent: a deploy reload must not reopen this form with it.
      forgetBootUrl();
      setDone(true);
    } catch (e2) {
      if (e2 instanceof ApiError && e2.code === 'invalid_token') {
        setInvalidLink(true);
      } else {
        setErr(e2 instanceof ApiError ? e2.message : 'Could not reset the password');
      }
    } finally {
      setLoading(false);
    }
  }

  const fieldWrap = 'flex items-center gap-2.5 h-11 px-3.5 rounded-xl border border-border bg-surface transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15';
  const fieldInput = 'login-field flex-1 min-w-0 bg-transparent outline-none text-[0.875rem] text-primary-text placeholder:text-muted';
  const labelCls = 'block text-[0.6875rem] font-semibold tracking-[0.14em] text-secondary uppercase';
  const submitBtn = 'login-shimmer relative overflow-hidden w-full h-12 rounded-xl text-white font-semibold text-[0.9375rem] inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 shadow-lg shadow-indigo-500/25 transition-all disabled:opacity-70 disabled:cursor-not-allowed';
  const errorBox = 'rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-body text-rose-700';

  return (
    <div className="relative min-h-screen min-h-[100dvh] flex flex-col items-center justify-center px-4 py-6 overflow-hidden bg-[#eef0fb]">
      {/* Same fixed indigo aurora as the sign-in page (reduce-motion stops it). */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="login-blob absolute -top-40 -left-32 h-[34rem] w-[34rem] rounded-full bg-indigo-500/25 blur-[80px]" />
        <div className="login-blob-2 absolute -bottom-48 -right-32 h-[38rem] w-[38rem] rounded-full bg-sky-400/25 blur-[80px]" />
        <div className="login-grid absolute inset-0 opacity-[0.5]"
          style={{ backgroundImage: 'linear-gradient(#6366f114 1px, transparent 1px), linear-gradient(90deg, #6366f114 1px, transparent 1px)', backgroundSize: '40px 40px', maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, #000 40%, transparent 100%)' }} />
      </div>

      <div className="relative w-full max-w-[420px] -mt-8">
        <div className="text-center mb-5 login-rise" style={{ animationDelay: '0.05s' }}>
          <h1 className="text-[1.75rem] sm:text-[1.875rem] font-bold tracking-tight text-[#1e1583] leading-[1.15]">
            Choose a new password
          </h1>
          <p className="text-[0.875rem] text-[#5b5a78] mt-2">for your {BRAND} account</p>
        </div>

        <div className="rounded-2xl border border-indigo-100 bg-white/95 backdrop-blur shadow-xl shadow-indigo-300/40 px-6 sm:px-8 py-6 login-rise" style={{ animationDelay: '0.18s' }}>
          <div className="flex flex-col items-center justify-center gap-2 mb-5">
            <span className="login-logo">
              <img src="/logo-fritech.png" alt={BRAND} className="h-12 w-auto select-none" draggable={false} />
            </span>
            <div className="text-[0.6875rem] font-semibold tracking-[0.12em] text-primary uppercase">{SLOGAN}</div>
          </div>

          {invalidLink ? (
            <div className="space-y-4">
              <div role="alert" className={errorBox}>
                This reset link is invalid or has expired. Reset links stay valid for 60 minutes
                and can be used once.
              </div>
              <button type="button" onClick={() => nav('/login?forgot=1')} className={submitBtn}>
                Request a new link <ArrowRight />
              </button>
            </div>
          ) : done ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-[#C2D8F2] bg-[#E0ECFB] px-3 py-2 text-body text-[#2D5596]">
                Your password has been updated. Sign in with the new password — any other open
                sessions have been signed out.
              </div>
              <button type="button" onClick={() => nav('/login')} className={submitBtn}>
                Go to sign in <ArrowRight />
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="reset-password-new" className={`${labelCls} mb-1.5`}>New password</label>
                <div className={fieldWrap}>
                  <span className="text-muted shrink-0"><LockIcon /></span>
                  <input id="reset-password-new" className={fieldInput} autoFocus type={showPw ? 'text' : 'password'} value={pw1}
                    onChange={(e) => setPw1(e.target.value)} autoComplete="new-password" placeholder="••••••••••" />
                  <button type="button" onClick={() => setShowPw((v) => !v)}
                    className="focus-ring rounded text-muted hover:text-primary-text shrink-0" aria-label={showPw ? 'Hide password' : 'Show password'}>
                    <EyeIcon off={showPw} />
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="reset-password-confirm" className={`${labelCls} mb-1.5`}>Confirm password</label>
                <div className={fieldWrap}>
                  <span className="text-muted shrink-0"><LockIcon /></span>
                  <input id="reset-password-confirm" className={fieldInput} type={showPw ? 'text' : 'password'} value={pw2}
                    onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" placeholder="••••••••••" />
                </div>
                <p className="text-[0.75rem] text-muted mt-2">At least 12 characters; not a common word, a date, or your email.</p>
              </div>

              {err && <div role="alert" className={errorBox}>{err}</div>}

              <button type="submit" disabled={loading} className={submitBtn}>
                {loading ? 'Saving…' : (<>Set new password <ArrowRight /></>)}
              </button>
            </form>
          )}

          <div className="mt-7 pt-4 text-center text-[0.75rem] text-muted">
            Secure login · {BRAND} cloud · © {new Date().getFullYear()} {BRAND}
          </div>
        </div>
      </div>
    </div>
  );
}
